/**
 * 「決着した行を一覧へ何日残すか」の共通ルール。
 *
 * デザイナー貢献度アンケート（デザイン view の子票行）と DLE 評価アンケート（開発 view の
 * 完了母票行）が同じ規則で行を落とす。**回答した直後に行が消えてはいけない**のが要点 —
 * 自分の回答が通ったことを一覧で確認できないと、送信できたのか分からないまま終わる。
 * 落とすのは確認期間を過ぎてからにする。
 *
 * 片方だけ日数を変えると「デザインは 1 週間残るのに DLE は即消える」形でズレるため、
 * 定義は 1 箇所に置く。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 決着した担当案件行の保持期間（1 週間）と、日付差・東京日付の共通ヘルパー
 * @graph-connects none
 */

/**
 * 決着後に一覧へ残す日数。これを**超えた**行を落とす（= 当日から 7 日目までは出る）。
 * @graph-connects none
 */
export const SETTLED_ROW_RETENTION_DAYS = 7;

/** 1 日のミリ秒。 @graph-connects none */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * 「YYYY-MM-DD」2 つの日数差（a - b）。解釈できない入力は null。
 * @graph-connects none
 */
export function daysBetween(a: string, b: string): number | null {
  const left = Date.parse(`${a}T00:00:00Z`);
  const right = Date.parse(`${b}T00:00:00Z`);
  if (Number.isNaN(left) || Number.isNaN(right)) return null;
  return (left - right) / MS_PER_DAY;
}

/**
 * 決着日から保持期間を過ぎたか。
 *
 * 決着日が取れない / 解釈できない場合は **false**（= 落とさない）。判定材料が無い行を
 * 黙って消すと、回答経路ごと消える事故になる。
 * @graph-connects daysBetween [calls] 決着日からの経過日数
 */
export function isPastRetention(settledOn: string | null, today: string): boolean {
  if (settledOn === null) return false;
  const elapsed = daysBetween(today, settledOn);
  if (elapsed === null) return false;
  return elapsed > SETTLED_ROW_RETENTION_DAYS;
}

/**
 * 回答済み行のうち、保持期間を過ぎて一覧から落としてよいもの。
 *
 * デザイナー貢献度アンケートと DLE 評価アンケートのタスクはどちらも
 * `{ state, respondedAt }` を持つので、構造的に受けて 1 箇所で判定する。
 * **未回答は常に false**（落とすと回答経路そのものが消える）。回答日が取れない
 * 回答済みも false（判定材料が無い行を黙って消さない）。
 * @graph-connects isPastRetention [calls] 回答日からの保持期間判定
 */
export function isStaleAnsweredRow(
  row: { state: 'pending' | 'answered'; respondedAt: string | null },
  today: string,
): boolean {
  if (row.state !== 'answered') return false;
  return isPastRetention(row.respondedAt?.slice(0, 10) ?? null, today);
}

/**
 * Asia/Tokyo の今日（"YYYY-MM-DD"）。
 * UTC で出すと日本時間の朝 9 時まで前日扱いになり、境界日の行が 1 日長く残る。
 * @graph-connects none
 */
export function tokyoToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tokyo' }).format(new Date());
}
